<template>
  <div>
    <div class="mb-4 flex justify-end" v-if="can('download-pdf-delivery-notes')">
      <button class="btn" @click="generatePDF">📄 Generate PDF</button>
    </div>

    <table class="min-w-full border border-gray-200 rounded-md">
      <thead class="bg-gray-50 text-left">
        <tr>
          <th class="px-3 py-2">Not Included</th>
          <th class="px-3 py-2">SKU</th>
          <th class="px-3 py-2">Product</th>
          <th class="px-3 py-2">Name & No.</th>
          <th class="px-3 py-2">Size</th>
          <th class="px-3 py-2">Original Qty</th>
          <th class="px-3 py-2">Available to Ship</th>
          <th class="px-3 py-2">Qty to Ship</th>
          <th class="px-3 py-2">Outstanding</th>
          <th class="px-3 py-2">Box #</th>
          <th class="px-3 py-2">Actions</th>
        </tr>
      </thead>

      <tbody>
        <!-- <CHANGE> unified grouping by factory_product_id for all rows  -->
        <template v-for="group in groupedByFactory" :key="group.key">
          <template v-if="group.mainChildren.length > 1">
            <!-- Group header row (expand/collapse + parent checkbox)  -->
            <tr
              class="border-t bg-gray-100 font-medium cursor-pointer"
              @click="toggleExpand(group.key)"
            >
              <td class="px-3 py-2">
                <div class="flex items-center gap-2">
                  <input
                    type="checkbox"
                    :checked="group.children.every((c) => !!c.not_included)"
                    @click.stop="onGroupNotIncludedChange($event, group)"
                  />
                  <button
                    class="text-sm w-6 h-6 flex items-center justify-center"
                    @click.stop="toggleExpand(group.key)"
                    aria-label="toggle"
                  >
                    <span v-if="expanded[group.key]"><ChevronUpIcon class="size-4" /></span>
                    <span v-else><ChevronDownIcon class="size-4" /></span>
                  </button>
                </div>
              </td>
              <td class="px-3 py-2" :colspan="8">
                {{ group.product_name }}{{ group.product_name && group.size ? ' - ' : ''
                }}{{ group.size }} ({{ group.children.length }} items)
              </td>

              <!-- group-level box select -->
              <td class="px-3 py-2" @click.stop>
                <Select
                  :options="boxOptionsWithLabels"
                  v-model="groupHeaderBox[group.key]"
                  label="displayLabel"
                  track-by="id"
                  :clearable="false"
                  class="min-w-[180px]"
                  @update:modelValue="onGroupBoxChange($event, group)"
                  :disabled="group.children.every((c) => !!c.not_included)"
                />
              </td>

              <td class="px-3 py-2"></td>
            </tr>
            <!-- Name/Number children (has_name_number = true), rendered as individual rows  -->
            <tr
              v-for="child in group.hnnChildren"
              v-show="expanded[group.key]"
              :key="'hnn-' + child.line_id"
              class="border-t"
            >
              <td class="px-3 py-2">
                <input
                  type="checkbox"
                  :checked="!!child.not_included"
                  @click.stop="onChildNotIncludedChange($event, child)"
                />
              </td>

              <td class="px-3 py-2">{{ child.sku_number }}</td>
              <td class="px-3 py-2">{{ child.product_name }}</td>
              <td class="px-3 py-2">
                {{ child.name || '' }} {{ child.name && child.number ? ' - ' : '' }}
                <span v-if="child.number"> {{ child.number }}</span>
              </td>
              <td class="px-3 py-2">{{ child.size }}</td>

              <td class="px-3 py-2">{{ child.qty_original }}</td>
              <td class="px-3 py-2">{{ child.qty_ordered }}</td>

              <!-- qty_to_ship for has_name_number child: validate by its own qty_ordered  -->
              <td class="px-3 py-2">
                <input
                  type="number"
                  v-model.number="child.qty_to_ship"
                  :max="child.qty_ordered"
                  min="0"
                  class="input"
                  :disabled="child.not_included"
                  @input="validateQty(child.line_id, child)"
                />
              </td>

              <!-- per-child outstanding  -->
              <td class="px-3 py-2">
                {{
                  (child.qty_ordered || 0) -
                  (child.not_included ? 0 : (child.qty_to_ship || 0))
                }}
              </td>

              <td class="px-3 py-2">
                <Select
                  :options="boxOptionsWithLabels"
                  v-model="child.box"
                  :clearable="false"
                  label="displayLabel"
                  :track-by="'id'"
                  class="min-w-[180px]"
                  :disabled="child.not_included"
                  @update:modelValue="onIndividualBoxChange(child)"
                />
              </td>

              <td class="px-3 py-2"></td>
            </tr>

            <!-- Non-Name/Number subgroups (has_name_number = false), grouped by line_id to preserve split/validation  -->
            <template v-for="sub in group.nonHnnGroups" :key="'sub-' + String(sub.line_id)">
              <tr
                v-for="(row, idx) in sub.children"
                v-show="expanded[group.key]"
                :key="'nnh-' + row.line_id + '-' + idx"
                class="border-t"
              >
                <template v-if="!row.isSplit && idx === 0">
                  <td class="px-3 py-2">
                    <input
                      type="checkbox"
                      :checked="sub.children.every((c) => !!c.not_included)"
                      @click.stop="onLineGroupNotIncludedChange($event, sub.children)"
                    />
                  </td>
                  <td class="px-3 py-2">{{ row.sku_number }}</td>
                  <td class="px-3 py-2">
                    {{ row.product_name ? `${row.product_name}` : '' }}
                  </td>
                  <td class="px-3 py-2">
                    {{ row.name?.length ? row.name : '' }}
                    <span v-if="row.name && row.number"> - </span>
                    {{ row.number ?? '' }}
                  </td>
                  <td class="px-3 py-2">{{ row.size }}</td>
                  <td class="px-3 py-2">{{ row.qty_original }}</td>
                  <td class="px-3 py-2">{{ row.qty_ordered }}</td>
                </template>

                <template v-else-if="row.isSplit">
                  <!-- split rows hide first 6 columns -->
                  <td colspan="7"></td>
                </template>

                <!-- qty to ship -->
                <td class="px-3 py-2">
                  <!-- <pre>{{ { isSplit: row.isSplit } }}</pre> -->
                  <input
                    type="number"
                    v-model.number="row.qty_to_ship"
                    :max="remainingQty(row.line_id, row)"
                    min="0"
                    class="input"
                    @input="validateQty(row.line_id, row)"
                    :disabled="row.not_included"
                  />
                </td>

                <!-- outstanding -->
                <td class="px-3 py-2">{{ calcOutstanding(row.line_id) }}</td>

                <!-- box -->
                <td class="px-3 py-2">
                  <Select
                    :options="boxOptionsWithLabels"
                    v-model="row.box"
                    label="displayLabel"
                    track-by="id"
                    :clearable="false"
                    class="min-w-[180px]"
                    :disabled="row.not_included"
                    @update:modelValue="onIndividualBoxChange(row)"
                  />
                </td>

                <!-- actions -->
                <td class="px-3 py-2 flex gap-2">
                  <button
                    v-if="!row.isSplit && idx === 0 && canSplit(row.line_id)"
                    class="btn"
                    @click="splitRow(row.line_id)"
                    :disabled="row.not_included"
                  >
                    + Split
                  </button>
                  <button
                    v-if="row.isSplit"
                    class="btn text-white bg-red-500"
                    @click="deleteRow(row.line_id, idx)"
                    :disabled="row.not_included"
                  >
                    🗑️
                  </button>
                </td>
              </tr>
            </template>
          </template>
          <template v-else>
            <!-- main row -->
            <tr class="border-t">
              <td class="px-3 py-2">
                <input
                  type="checkbox"
                  v-model="group.children[0].not_included"
                  @change="(e) => toggleNotIncluded(e, group.children[0])"
                />
              </td>
              <td class="px-3 py-2">{{ group.children[0].sku_number }}</td>
              <td class="px-3 py-2">
                {{ group.children[0].product_name ? `${group.children[0].product_name}` : '' }}
              </td>
              <td class="px-3 py-2">
                {{ group.children[0].name ? group.children[0].name : '' }}
                <span v-if="group.children[0].name && group.children[0].number">-</span>
                {{ group.children[0].number ? group.children[0].number : '' }}
              </td>
              <td class="px-3 py-2">{{ group.children[0].size }}</td>
              <td class="px-3 py-2">{{ group.children[0].qty_original }}</td>
              <td class="px-3 py-2">{{ group.children[0].qty_ordered }}</td>
              <td class="px-3 py-2">
                <input
                  type="number"
                  v-model.number="group.children[0].qty_to_ship"
                  :max="remainingQty(group.children[0].line_id, group.children[0])"
                  min="0"
                  class="input"
                  @input="validateQty(group.children[0].line_id, group.children[0])"
                  :disabled="group.children[0].not_included"
                />
              </td>
              <td class="px-3 py-2">
                <template v-if="group.children[0].has_name_number">
                  {{
                    (group.children[0].qty_ordered || 0) -
                    (group.children[0].not_included ? 0 : (group.children[0].qty_to_ship || 0))
                  }}
                </template>
                <template v-else>
                  {{ calcOutstanding(group.children[0].line_id) }}
                </template>
              </td>
              <td class="px-3 py-2">
                <Select
                  :options="boxOptionsWithLabels"
                  v-model="group.children[0].box"
                  label="displayLabel"
                  track-by="id"
                  :clearable="false"
                  class="min-w-[180px]"
                  :disabled="group.children[0].not_included"
                  @update:modelValue="onIndividualBoxChange(group.children[0])"
                />
              </td>
              <td class="px-3 py-2 flex gap-2">
                <button
                  v-if="!group.children[0].has_name_number && canSplit(group.children[0].line_id)"
                  class="btn"
                  @click.stop="splitRow(group.children[0].line_id)"
                  :disabled="group.children[0].not_included"
                >
                  + Split
                </button>

                <button
                  class="btn text-white bg-red-500"
                  @click="deleteRow(group.children[0].line_id, 0)"
                  v-if="group.children.slice(1).length > 1"
                  :disabled="group.children[0].not_included"
                >
                  🗑️
                </button>
              </td>
            </tr>

            <!-- render split rows if any -->
            <tr
              v-for="(row, idx) in group.children.slice(1)"
              :key="'split-' + row.line_id + '-' + idx"
              class="border-t"
            >
              <!-- hide main info -->
              <td colspan="7"></td>

              <!-- qty to ship -->
              <td class="px-3 py-2">
                <input
                  type="number"
                  v-model.number="row.qty_to_ship"
                  :max="remainingQty(row.line_id, row)"
                  min="0"
                  class="input"
                  @input="validateQty(row.line_id, row)"
                />
              </td>

              <!-- outstanding -->
              <td class="px-3 py-2">{{ calcOutstanding(row.line_id) }}</td>

              <!-- box -->
              <td class="px-3 py-2">
                <Select
                  :options="boxOptionsWithLabels"
                  v-model="row.box"
                  label="displayLabel"
                  track-by="id"
                  :clearable="false"
                  class="min-w-[180px]"
                  @update:modelValue="onIndividualBoxChange(row)"
                />
              </td>

              <!-- delete button only for split -->
              <td class="px-3 py-2 flex gap-2">
                <button class="btn text-white bg-red-500" @click="deleteRow(row.line_id, idx + 1)">
                  🗑️
                </button>
              </td>
            </tr>
          </template>
        </template>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import Select from '@/components/Shared/Select.vue';
import { can } from '@/composables';
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-vue-next';
import { computed, ref, watch } from 'vue';
import { generateDeliveryPdf, sortRowsByBox } from './usePDFexport';

interface LineItem {
  line_id: number;
  factory_product_id: string;
  product_name: string;
  size: string;
  sku_number: number | string;
  has_name_number: boolean;
  name?: string;
  number?: string;
  qty_original?: number;
  qty_ordered: number;
  qty_to_ship: number;
  box: any | null;
  qty_outstanding?: number;
  not_included?: boolean;
  isSplit?: boolean;
}

interface Box {
  id: number | string;
  label: string;
  box_id: number | string;
  length?: string;
  width?: string;
  height?: string;
  weight?: string;
  is_custom_box?: boolean;
}

const props = defineProps<{
  items: LineItem[];
  boxes: Box[];
  shipmentAddress: any;
  order_info: any
}>();

const emit = defineEmits(['update:items']);
const rows = ref<LineItem[]>(props.items ? [...props.items] : []);
const groupHeaderBox = ref<Record<string, any>>({});
const boxAssignmentOrder = ref<Map<string, number>>(new Map());

watch(
  () => rows.value.map(r => r.box?.box_id),
  () => {
    // This will trigger re-computation of sortedRows and groupedByFactory
    // The state is preserved because we're not modifying the rows
  },
  { deep: true }
);

watch(
  () => props.items,
  (newVal) => {
    rows.value = Array.isArray(newVal) ? [...newVal] : [];

    // Track existing box assignments on initial load
    const existingBoxes = new Set<string>();
    rows.value.forEach(r => {
      if (r.box) {
        const boxLabel = r.box.label || '';
        const boxId = r.box.box_id || '';
        const combined = boxLabel && boxId ? `${boxLabel} (${boxId})` : '';
        if (combined) {
          existingBoxes.add(combined);
        }
      }
    });

    // Add existing boxes to assignment order if not already there
    existingBoxes.forEach(combined => {
      if (!boxAssignmentOrder.value.has(combined)) {
        boxAssignmentOrder.value.set(combined, boxAssignmentOrder.value.size);
      }
    });

    const byLine = new Map<number, LineItem[]>();
    for (const r of rows.value) {
      if (!byLine.has(r.line_id)) byLine.set(r.line_id, []);
      byLine.get(r.line_id)!.push(r);
    }

    for (const group of byLine.values()) {
      const hasHnn = group.some(r => r.has_name_number);

      if (!hasHnn) {
        const allZero = group.every(r => !r.not_included && (!r.qty_to_ship || r.qty_to_ship === 0));
        if (allZero && group.length > 0) {
          group[0].qty_to_ship = group[0].qty_ordered;
        }
      } else {
        group.forEach(r => {
          if (!r.not_included && (!r.qty_to_ship || r.qty_to_ship === 0)) {
            r.qty_to_ship = r.qty_ordered;
          }
        });
      }
    }

    emitUpdate();
  },
  { immediate: true }
);

const boxOptionsWithLabels = computed(() => {
  return props.boxes.map(box => ({
    ...box,
    displayLabel: box.box_id ? `${box.label} (${box.box_id})` : box.label
  }));
});

const sortedRows = computed(() => {
  // Create a copy to avoid mutating the original
  const rowsCopy = [...rows.value];
  const rowsSort = sortRowsByBox(rowsCopy, boxAssignmentOrder.value);
  return rowsSort;
});

const groupedByFactory = computed(() => {
  const byFactory = new Map<string, LineItem[]>();

  for (const r of sortedRows.value) {
    const key = String(r.factory_product_id);
    if (!byFactory.has(key)) byFactory.set(key, []);
    byFactory.get(key)!.push(r);
  }

  return Array.from(byFactory.entries()).map(([fp, children]) => {
    const hnnChildren = children.filter((c) => c.has_name_number);

    const nonHnn = children.filter((c) => !c.has_name_number);
    const byLine = new Map<number, LineItem[]>();

    for (const r of nonHnn) {
      const k = r.line_id;
      if (!byLine.has(k)) byLine.set(k, []);
      byLine.get(k)!.push(r);
    }

    const nonHnnGroups = Array.from(byLine.entries()).map(([line_id, items]) => {
      // ensure consistent order
      const sorted = [...items];
      // mark all but first as isSplit
      sorted.forEach((item, idx) => {
        item.isSplit = idx > 0;
      });
      return {
        line_id,
        children: sorted,
        mainChildren: sorted.filter((c) => !c.isSplit),
      };
    });

    return {
      key: `fp:${fp}`,
      factory_product_id: fp,
      product_name: children[0]?.product_name,
      size: children[0]?.size,
      children,
      hnnChildren,
      nonHnnGroups,
      mainChildren: children.filter((c) => !c.isSplit),
    };
  });
});

const expanded = ref<Record<string, boolean>>({});

function toggleExpand(key: string) {
  expanded.value[key] = !expanded.value[key];
}

function onGroupBoxChange(newBox: any, group: { children: LineItem[]; key: string }) {
  groupHeaderBox.value[group.key] = newBox;

  // Track assignment order - only add if not already tracked
  if (newBox) {
    const boxLabel = newBox.label || '';
    const boxId = newBox.box_id || '';
    const combined = boxLabel && boxId ? `${boxLabel} (${boxId})` : '';

    if (combined && !boxAssignmentOrder.value.has(combined)) {
      boxAssignmentOrder.value.set(combined, boxAssignmentOrder.value.size);
    }
  }

  for (const child of group.children) {
    child.box = newBox;
  }

  // Sort after changing group box
  //sortRowsByBox();
  emitUpdate();
}

/* ---- Utilities for non-HNN line groups (split behavior preserved) ---- */
function findNormalGroupRows(line_id: number) {
  return rows.value.filter((r) => r.line_id === line_id && !r.has_name_number);
}

// Compute outstanding for a line group (non-HNN)
function calcOutstanding(line_id: number) {
  const group = findNormalGroupRows(line_id);
  if (!group.length) return 0;
  const orderQty = group[0].qty_ordered || 0;
  const shipped = group.reduce((s, r) => s + (r.not_included ? 0 : (Number(r.qty_to_ship) || 0)), 0);
  return Math.max(orderQty - shipped, 0);
}

// Remaining allowed for a row in a non-HNN line group
function remainingQty(line_id: number, currentRow: LineItem) {
  const group = findNormalGroupRows(line_id);
  if (!group.length) return 0;
  const orderQty = group[0].qty_ordered || 0;
  const other = group.reduce((s, r) => {
    if (r === currentRow) return s;
    return s + (r.not_included ? 0 : (Number(r.qty_to_ship) || 0));
  }, 0);
  return Math.max(orderQty - other, 0);
}

// <CHANGE> keep original factory_product_id on splits so they remain in same factory group
function splitRow(line_id: number) {
  const group = findNormalGroupRows(line_id);
  if (!group.length) return;
  const parent = group[0];
  if (group.length < (parent.qty_ordered || 0)) {
    const newRow: LineItem = {
      ...parent,
      qty_to_ship: 0,
      box: null,
      isSplit: true, // <--- NEW FLAG
    };
    rows.value.push(newRow);
    emitUpdate();
  }
}

function deleteRow(line_id: number, index: number) {
  const group = findNormalGroupRows(line_id);
  const row = group[index];
  if (!row) return;

  if (index > 0) {
    rows.value = rows.value.filter((r) => r !== row);
  } else if (group.length > 1) {
    const next = group[1];
    const parentIndex = rows.value.indexOf(group[0]);
    if (parentIndex !== -1) {
      rows.value.splice(parentIndex, 1, { ...next });
      const childIndex = rows.value.indexOf(next);
      if (childIndex !== -1) rows.value.splice(childIndex, 1);
    }
  }
  emitUpdate();
}

function validateQty(line_id: number, row: LineItem) {
  // For HNN rows, remainingQty returns based on non-HNN groups, so cap manually by own qty_ordered
  if (row.has_name_number) {
    const max = Number(row.qty_ordered) || 0;
    if (Number(row.qty_to_ship) > max) row.qty_to_ship = max;
    if (Number(row.qty_to_ship) < 0) row.qty_to_ship = 0;
  } else {
    const max = remainingQty(line_id, row);
    if (Number(row.qty_to_ship) > max) row.qty_to_ship = max;
    if (Number(row.qty_to_ship) < 0) row.qty_to_ship = 0;
  }
  emitUpdate();
}

function canSplit(line_id: number) {
  const group = findNormalGroupRows(line_id);
  if (!group.length) return false;
  return (group[0].qty_ordered || 0) > 1 && group.length < (group[0].qty_ordered || 0);
}

/* ---- Not included toggles ---- */
function onGroupNotIncludedChange(e: Event, group: { children: LineItem[] }) {
  const checked = (e.target as HTMLInputElement).checked;
  for (const child of group.children) {
    child.not_included = checked;
    if (checked) {
      child.box = null;
    }
  }
  emitUpdate();
}

function toggleNotIncluded(e: Event, child: LineItem) {
  const checked = (e.target as HTMLInputElement).checked;
  child.not_included = checked;
  if (checked) {
    child.box = null;
  }
  emitUpdate();
}

function onChildNotIncludedChange(e: Event, child: LineItem) {
  const checked = (e.target as HTMLInputElement).checked;
  child.not_included = checked;
  if (checked) {
    child.box = null;
  }
  emitUpdate();
}

function onLineGroupNotIncludedChange(e: Event, children: LineItem[]) {
  const checked = (e.target as HTMLInputElement).checked;
  for (const child of children) {
    child.not_included = checked;
    if (checked) {
      child.box = null;
    }
  }
  emitUpdate();
}

/* ---- Helpers ---- */
function emitUpdate() {
  emit('update:items', exportRows());
}

function exportRows() {
  return rows.value;
}

function onIndividualBoxChange(row: LineItem) {
  // Track assignment order - only add if not already tracked
  if (row.box) {
    const boxLabel = row.box.label || '';
    const boxId = row.box.box_id || '';
    const combined = boxLabel && boxId ? `${boxLabel} (${boxId})` : '';

    if (combined && !boxAssignmentOrder.value.has(combined)) {
      boxAssignmentOrder.value.set(combined, boxAssignmentOrder.value.size);
    }
  }

  // Sort rows by box label and box_id whenever a box is selected
  //sortRowsByBox();
  emitUpdate();
}

/* ---- PDF ---- */
const generatePDF = () => {
  const sortedForPdf = sortRowsByBox(rows.value, boxAssignmentOrder.value);
  const doc = generateDeliveryPdf(props.shipmentAddress, sortedForPdf, props.order_info);
  doc.save('deliver-notes.pdf');
};


defineExpose({ rows, exportRows });
</script>

<style lang="scss" scoped>
.btn {
  @apply px-2 py-2 flex gap-2 justify-between items-center rounded-lg capitalize text-white bg-primary text-nowrap disabled:opacity-75 disabled:cursor-not-allowed;
}
.input {
  @apply mt-1 block w-full rounded-[4px] outline-none px-2 py-1 border-[1px] border-[#3c3c3c42] focus:border-[#3c3c3c];
}
</style>
